import { useState } from 'react';
import { SuccessModal } from './SuccessModal';
import { useLanguageStore } from '../store/languageStore';
import { useAuthStore } from '../store/authStore';
import { getLogoPath } from '../utils/logoUtils';
import { clearStoredAdminInterface } from '../utils/adminInterfaceSelection';

interface AdminEntryPointPickerProps {
  onOfficeSelect: () => void;
  onFieldWorkerSelect: () => void;
}

const ADMIN_ENTRY_OPTIONS = [
  {
    id: 'office',
    title: 'משרד',
    description: 'כניסה לניהול משרדי',
  },
  {
    id: 'field-worker',
    title: 'איש שטח',
    description: 'ניהול שיבוצים וחסימות מהשטח',
  },
  {
    id: 'payroll',
    title: 'חשבת שכר',
    description: 'מסך זה יעודכן בקרוב',
  },
  {
    id: 'ceo',
    title: 'מנכ״ל',
    description: 'מסך זה יעודכן בקרוב',
  },
] as const;

function EntryIcon({ optionId }: { optionId: (typeof ADMIN_ENTRY_OPTIONS)[number]['id'] }) {
  if (optionId === 'office') {
    return (
      <svg className="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.8}
          d="M3 21h18M5 21V7a1 1 0 011-1h5v15M14 21V4a1 1 0 011-1h3a1 1 0 011 1v17"
        />
      </svg>
    );
  }

  if (optionId === 'field-worker') {
    return (
      <svg className="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.8}
          d="M12 3v7m0 0c2.5 0 4.5 2 4.5 4.5V21h-9v-6.5C7.5 12 9.5 10 12 10zm-4.5 5.5h9"
        />
      </svg>
    );
  }

  if (optionId === 'payroll') {
    return (
      <svg className="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.8}
          d="M7 3h10a1 1 0 011 1v16l-2.5-1.5L13 20l-2.5-1.5L8 20V4a1 1 0 011-1zM9 8h6M9 12h6"
        />
      </svg>
    );
  }

  return (
    <svg className="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.8}
        d="M4 20h16M6 16l3-4 3 2 4-6 2 3"
      />
      <circle cx="6" cy="16" r="1" fill="currentColor" />
      <circle cx="9" cy="12" r="1" fill="currentColor" />
      <circle cx="12" cy="14" r="1" fill="currentColor" />
      <circle cx="16" cy="8" r="1" fill="currentColor" />
      <circle cx="18" cy="11" r="1" fill="currentColor" />
    </svg>
  );
}

export function AdminEntryPointPicker({ onOfficeSelect, onFieldWorkerSelect }: AdminEntryPointPickerProps) {
  const language = useLanguageStore((state) => state.language);
  const logout = useAuthStore((state) => state.logout);
  const [isComingSoonOpen, setIsComingSoonOpen] = useState(false);

  const handleSelect = (optionId: (typeof ADMIN_ENTRY_OPTIONS)[number]['id']) => {
    if (optionId === 'office') {
      onOfficeSelect();
      return;
    }

    if (optionId === 'field-worker') {
      onFieldWorkerSelect();
      return;
    }

    setIsComingSoonOpen(true);
  };

  const handleBackToLogin = () => {
    clearStoredAdminInterface();
    logout();
  };

  return (
    <>
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 px-4 py-6">
        <div className="w-full max-w-4xl rounded-2xl bg-white/95 shadow-xl border border-white backdrop-blur p-6 md:p-10">
          <div className="text-center mb-8">
            <img src={getLogoPath(language)} alt="לוריא דנאור" className="h-16 mx-auto mb-5" />
            <h1 className="text-3xl md:text-4xl font-bold text-slate-800 mb-2">בחירת ממשק מנהל</h1>
            <p className="text-slate-600 text-base md:text-lg">בחר את סוג ההתחברות הרצוי</p>
            <button
              onClick={handleBackToLogin}
              className="mt-4 inline-flex items-center rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
            >
              חזור להתחברות
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {ADMIN_ENTRY_OPTIONS.map((option) => (
              <button
                key={option.id}
                onClick={() => handleSelect(option.id)}
                className="w-full text-right rounded-xl border border-slate-200 bg-white hover:bg-blue-50 hover:border-blue-300 transition-all duration-200 shadow-sm hover:shadow-md p-5 flex items-start gap-4"
              >
                <span className="mt-0.5 flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-blue-100">
                  <EntryIcon optionId={option.id} />
                </span>
                <span className="flex flex-col">
                  <span className="text-xl font-semibold text-slate-800">{option.title}</span>
                  <span className="text-sm text-slate-600 mt-1">{option.description}</span>
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>

      <SuccessModal
        isOpen={isComingSoonOpen}
        onClose={() => setIsComingSoonOpen(false)}
        title="בקרוב"
        message="מסך זה יעודכן בקרוב..."
        buttonText="סגירה"
      />
    </>
  );
}
